import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import FoodMenu from "./food-menu";
import DrinksMenu from "./drinks-menu";
import CartModal from "./cart-modal";
import OrderConfirmation from "./order-confirmation";
import { usePosCart } from "@/hooks/pos/use-cart";
import { Backpack, ChevronLeft, UtensilsCrossed, Wine } from "lucide-react";
import { MenuTypesWithPoduct } from "@/types/pos";
import OrderStatusBanner from "./order-status-banner";
import { usePosOrders } from "@/hooks/pos/use-order";

interface MainMenuProps {
	location: {
		id: number;
		name?: string;
		details?: string;
		menuTypes?: MenuTypesWithPoduct[];
		isLoading?: boolean;
	};
	onBackToLocation: () => void;
}


export default function MainMenu({ location, onBackToLocation }: MainMenuProps) {
	const [, navigate] = useLocation();
	const [activeTab, setActiveTab] = useState<string>("food");
	const [showCart, setShowCart] = useState(false);
	const [showOrderConfirmation, setShowOrderConfirmation] = useState(false);
	const [confirmedOrder, setConfirmedOrder] = useState<any>(null);
	const [isDrinkBuilderOpen, setIsDrinkBuilderOpen] = useState(false);

	const { cartItems, sechUser, getCartItemCount } = usePosCart();

	const { orders } = usePosOrders();

	const handleBackToMenu = () => {
		setShowOrderConfirmation(false);
		setConfirmedOrder(null);
	};

	if(showOrderConfirmation && confirmedOrder) {
		return (
			<OrderConfirmation
				order={confirmedOrder}
				onBackToMenu={handleBackToMenu}
			/>
		);
	}

	const foodMenuTypes = location.menuTypes?.map(mt => ({
		...mt,
		items: mt.items.filter(i => i.category === "food"),
	})).filter(mt => mt.items.length > 0) ?? [];

	const drinksMenuTypes = location.menuTypes?.map(mt => ({
		...mt,
		items: mt.items.filter(i => i.category === "drink"),
	}))
		.filter(mt => mt.items.length > 0) ?? [];

	const hasFoodItems = foodMenuTypes.length > 0;
	const hasDrinkItems = drinksMenuTypes.length > 0;

	// Auto-switch to drinks tab if no food items but drinks are available
	// Or redirect back if both are empty
	useEffect(() => {
		if (!location.isLoading) {
			if (!hasFoodItems && !hasDrinkItems) {
				// No items available at all, redirect back
				onBackToLocation();
			} else if (!hasFoodItems && hasDrinkItems) {
				setActiveTab("drinks");
			} else if (hasFoodItems && !hasDrinkItems) {
				setActiveTab("food");
			}
		}
	}, [location.isLoading, hasFoodItems, hasDrinkItems, onBackToLocation]);

	const isCartOrderVisible =
		!isDrinkBuilderOpen &&
		(
			cartItems.length > 0 ||
			(orders.length > 0 && orders.some(o => o.status !== "delivered"))
		);

	return (
		<>
			<div className="bg-[var(--pos-secondary-background-color)] h-screen max-w-5xl mx-auto">
				{/* Header */}
				{!isDrinkBuilderOpen && (
					<>
						<div className="w-full max-w-5xl">
							<div className="pt-5 pl-5 pr-5">
								<div className="flex items-center">
									<div className="flex items-center gap-3 w-full">
										<Button
											variant="ghost"
											size="sm"
											onClick={onBackToLocation}
											className="h-8 w-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/40"
										>
											<ChevronLeft className="h-[22px] w-[22px] text-[#ffffff]"/>
										</Button>
										<span className="font-montserrat text-xl font-bold leading-[22px] text-[var(--pos-primary-text-color)]">
											{location?.name} {location?.details && location?.details}
										</span>
									</div>
									<div className="flex justify-end items-center w-full gap-1 cursor-pointer" onClick={() => navigate('/order/history')}>
										<span className="text-[12px] leading-[18px] font-figtree font-medium text-[var(--pos-primary-text-color)]">Order History</span>
										<button className="rounded-full bg-white/10 h-[28px] w-[28px] flex items-center justify-center"><Backpack className="text-[var(--pos-primary-text-color)] w-5 h-5" /></button>
									</div>
								</div>
							</div>

							{/* Tab Navigation */}
							<div className="mt-4 mb-[6px] px-2">
								<div className="p-1 relative flex justify-between items-center rounded-lg bg-[var(--pos-primary-background-color)] ">
									<button
										onClick={() => hasFoodItems && setActiveTab("food")}
										disabled={!hasFoodItems}
										className={`font-figtree w-1/2 h-[40px] z-[1] rounded-xl flex items-center justify-center gap-1 text-[13px] font-medium leading-[15px] ${
											!hasFoodItems
												? "text-gray-500 opacity-50 cursor-not-allowed"
												: activeTab === "food"
													? "active-tab text-[var(--pos-primary-text-color)]"
													: "text-[var(--pos-primary-text-color)]"
										}`}
									>
										<UtensilsCrossed className={`w-5 h-5 ${!hasFoodItems ? "text-gray-500" : activeTab === "food" ? "active-tab text-[var(--pos-primary-text-color)]" : "text-[var(--pos-primary-text-color)]"}`}/>
										Food
									</button>
									<button
										onClick={() => hasDrinkItems && setActiveTab("drinks")}
										disabled={!hasDrinkItems}
										className={`w-1/2 font-figtree h-[40px] z-[1] rounded-xl flex items-center justify-center gap-1 text-[13px] font-medium leading-[15px] ${
											!hasDrinkItems
												? "text-gray-500 opacity-50 cursor-not-allowed"
												: activeTab === "drinks"
													? "active-tab text-[var(--pos-primary-text-color)]"
													: "text-[var(--pos-primary-text-color)]"
										}`}
									>
										<Wine className={`w-5 h-5 ${!hasDrinkItems ? "text-gray-500" : activeTab === "drinks" ? "active-tab" : ""}`}/>
										Drinks
									</button>
								</div>
							</div>
						</div>
					</>
				)}

				{/* Menu Content */}
				<div className={`h-[calc(100%-148px)]`}>
					{activeTab === "food" ? <FoodMenu selectedLocation={location?.name!} menuTypes={foodMenuTypes} isLoading={location?.isLoading} isCartOrderVisible={isCartOrderVisible}/> : <DrinksMenu selectedLocation={location?.name!} menuTypes={drinksMenuTypes} isLoading={location?.isLoading}  onOpenDrinkBuilder={() => setIsDrinkBuilderOpen(true)} onCloseDrinkBuilder={() => setIsDrinkBuilderOpen(false)} isCartOrderVisible={isCartOrderVisible} />}
				</div>
				{!isDrinkBuilderOpen && !cartItems.length && (
					<OrderStatusBanner orders={orders} onOpenHistory={() => navigate('/order/history')} />
				)}
				{/* Sticky Cart Button */}
				{!isDrinkBuilderOpen && cartItems.length > 0 && (
					<div className="fixed bottom-[68px] left-0 right-0 z-40">
						<div className="max-w-5xl mx-auto px-5">
							<div className="rounded-[8px] h-12 p-[7px] flex items-center justify-between bg-[var(--pos-inverted-background-color)]">
								<div className="flex gap-1 items-center">
									<span className="h-[34px] w-[34px] font-medium flex rounded-full items-center justify-center bg-[--pos-primary-color] text-[13px] leading-[18px] font-montserrat text-white">
										{getCartItemCount()}
									</span>
									<span className="font-montserrat text-[13px] leading-[18px] font-medium text-white">Items added</span>
								</div>
								<div className="flex items-center gap-2">
									<Button
										onClick={() => setShowCart(true)}
										className="bg-[--pos-primary-color] h-8 rounded-lg flex items-center justify-center px-[10px] font-montserrat text-[13px] leading-[18px] font-semibold text-[#ffffff]"
									>
										View Cart
									</Button>
								</div>
							</div>
						</div>
					</div>
				)}
			</div>

			{/* Cart Modal */}
			{showCart && cartItems?.length && (
				<div className="fixed top-0 right-0 w-full h-full bg-[var(--pos-secondary-background-color)] z-50 animate-slideIn overflow-auto">
					<CartModal
						isOpen={showCart}
						onClose={() => setShowCart(false)}
					/>
				</div>
			)}
		</>
	);
}
